import {
  Box,
  BoxProps,
  LoadingView,
  MaintenanceScreen,
  NotificationContainer as NotifContainerV2,
  RequireFlag,
  useFeatureFlag
} from '@payflip/ui/v2';
import { UnknownAccountPage, useAuth } from '@payflip/web/auth';
import { useBootIntercom } from '@payflip/web/intercom';
import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import { environment } from '../../environment';
import { Language } from '../../generated/types';
import { GlobalModals } from '../../global-modals';
import { useMobileIntercom } from '../../hooks/intercom';
import { EmailNotVerifiedPage } from '../../screens/email-not-verified/email-not-verified';
import { AppShell } from '../app-shell';
import { GlobalHead } from '../global-head';
import { GlobalStyle } from '../global-style';
import { ImpersonateBanner } from '../impersonate/impersonate-banner';
import { Outlet } from '../outlet';
import { NotificationContainer } from '../toast-renderer';
import { AppOverlaySection } from './app-overlay-section';
import { useGetMaintenanceScreenQuery } from './queries.cms.generated';
import { useGetCurrentUserLazyQuery } from './queries.generated';

const Home = lazy(() => import('../../screens/home'));
const YourSpace = lazy(() => import('../../screens/your-space'));
const MoreInfo = lazy(() => import('../../screens/more-info'));
const YourBudgets = lazy(() => import('../../screens/your-budgets'));
const YourDocuments = lazy(() => import('../../screens/your-documents'));
const Settings = lazy(() => import('../../screens/settings'));
const Products = lazy(() => import('../../screens/products'));
const Cart = lazy(() => import('../../screens/cart'));
const AccessDenied = lazy(() => import('../../screens/access-denied'));
const Stocks = lazy(() => import('../../screens/stocks'));

function Wrapper(props: BoxProps): JSX.Element {
  return (
    <Box
      w="100vw"
      h="100dvh"
      sx={{ '@supports not (height: 100dvh)': { height: '100%' } }}
      overflow="hidden"
      display="flex"
      flexDirection={{ base: 'column', md: 'row' }}
      {...props}
    />
  );
}

export function Screen(props: BoxProps): JSX.Element {
  return (
    <Box
      w={{ base: '100%', md: 'unset' }}
      h={{ md: '100%' }}
      // Because the tab bar is fixed, we add a padding of it's height.
      pb={{ base: '60px', md: 0 }}
      flex={1}
      overflowY="auto"
      {...props}
    />
  );
}

function InnerApp(): JSX.Element | null {
  const isMaintenanceFlagOn = useFeatureFlag('maintenance');

  const { isLoading, isAuthenticated, signIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [getCurrentUser, getCurrentUserQuery] = useGetCurrentUserLazyQuery();
  const user = getCurrentUserQuery.data?.user;
  const isImpersonating = user?.isImpersonated ?? false;

  const { data: cmsData, isLoading: isCmsLoading } =
    useGetMaintenanceScreenQuery();
  const maintenanceScreenTranslations =
    cmsData?.maintenanceScreen?.data?.attributes;

  const { ready: intercomReady } = useBootIntercom({
    config: {
      appId: environment.INTERCOM_APP_ID,
      restApiUri: environment.REST_API_URI,
      userId: user?.id ?? '',
      username: user ? `${user.firstname} ${user.lastname}` : '',
      userLng: user?.language ?? Language.En,
      userEmail: user?.email ?? undefined
    },
    skip: isImpersonating || !user
  });

  useMobileIntercom(intercomReady);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const errorParam = params.get('error');

    if (isAuthenticated === true || isLoading === true || errorParam) {
      return;
    }

    window.sessionStorage.setItem(
      'pre-auth-url',
      `${location.pathname}${location.search}`
    );

    signIn();
  }, [isAuthenticated, isLoading, location, signIn]);

  useEffect(() => {
    if (isAuthenticated === false) {
      return;
    }

    const preAuthUrl = window.sessionStorage.getItem('pre-auth-url');
    window.sessionStorage.removeItem('pre-auth-url');

    if (typeof preAuthUrl === 'string') {
      navigate(preAuthUrl);
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated === false) {
      return;
    }

    getCurrentUser();
  }, [isAuthenticated, getCurrentUser]);

  const params = new URLSearchParams(window.location.search);

  const errorParam = params.get('error');

  const ldClient = useLDClient();

  useEffect(() => {
    if (user && ldClient) {
      ldClient.identify({
        kind: 'user',
        key: `user-key-${user.id}`,
        email: user.email
      });
    }
  }, [user, ldClient]);

  if (isMaintenanceFlagOn) {
    if (isCmsLoading) {
      return null;
    }
    return (
      <MaintenanceScreen
        title={maintenanceScreenTranslations?.title ?? ''}
        body={maintenanceScreenTranslations?.body ?? ''}
        logoUrl={
          maintenanceScreenTranslations?.payflipLogo?.data?.attributes?.url ??
          ''
        }
        imageUrl={
          maintenanceScreenTranslations?.maintenanceImage?.data?.attributes
            ?.url ?? ''
        }
      />
    );
  }

  if (errorParam === 'access_denied') {
    return (
      <>
        <GlobalHead />
        <GlobalStyle />
        <AppOverlaySection>
          <Wrapper>
            <Screen>
              <Suspense fallback={<LoadingView />}>
                <AccessDenied />
              </Suspense>
            </Screen>
          </Wrapper>
        </AppOverlaySection>
      </>
    );
  }

  if (isLoading) {
    return <LoadingView fixed />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <GlobalHead />
      <GlobalStyle />
      <ImpersonateBanner />
      <GlobalModals />
      <AppOverlaySection>
        <Wrapper>
          <Suspense fallback={<LoadingView />}>
            <Routes>
              <Route path="/unknown-user" element={<UnknownAccountPage />} />
              <Route
                path="/email-not-verified"
                element={<EmailNotVerifiedPage />}
              />
              <Route element={<AppShell />}>
                <Route path="/" element={<Home />}>
                  <Route path="request-cashout">
                    <Route
                      index
                      element={
                        <RequireFlag
                          flag="requestCashoutEmployeeClient"
                          redirectTo="/"
                        >
                          <Outlet type="REQUEST_CASHOUT_BUDGET_LIST" />
                        </RequireFlag>
                      }
                    />
                    <Route
                      path=":budgetId"
                      element={
                        <RequireFlag
                          flag="requestCashoutEmployeeClient"
                          redirectTo="/"
                        >
                          <Outlet type="REQUEST_CASHOUT_AMOUNT" />
                        </RequireFlag>
                      }
                    />
                  </Route>
                  <Route
                    path="activate-budget/:budgetId"
                    element={<Outlet type="BUDGET_ACTIVATION" />}
                  />
                  <Route
                    path="activate-budget/:budgetId/manual-signing/:templateId"
                    element={<Outlet type="MANUAL_BUDGET_ACTIVATION" />}
                  />
                </Route>
                <Route path="benefits">
                  <Route index element={<Products />} />

                  <Route path="products/:productId" element={<Products />} />
                  <Route path="providers/:providerId" element={<Products />} />
                  <Route path="details/:choiceId" element={<Products />} />
                  <Route
                    path="details/:choiceId/funding-edit"
                    element={<Products />}
                  />
                </Route>
                <Route path="cart">
                  <Route index element={<Cart />} />
                  <Route path="edit/:choiceId" element={<Cart />} />
                  <Route path="remove/:choiceId" element={<Cart />} />
                </Route>
                <Route path="your-space">
                  <Route index element={<YourSpace />} />
                  <Route path="more-info" element={<MoreInfo />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="budgets">
                    <Route index element={<YourBudgets />} />
                    <Route path=":budgetId" element={<YourBudgets />}>
                      <Route
                        path="activation"
                        element={<Outlet type="BUDGET_ACTIVATION" />}
                      />
                      <Route
                        path="activation/manual-signing/:templateId"
                        element={<Outlet type="MANUAL_BUDGET_ACTIVATION" />}
                      />
                      <Route
                        path=":choiceId/details"
                        element={<Outlet type="CHOICE_DETAILS" />}
                      />
                      <Route
                        path="request-cashout"
                        element={
                          <RequireFlag
                            flag="requestCashoutEmployeeClient"
                            redirectTo="/your-space"
                          >
                            <Outlet type="REQUEST_CASHOUT_AMOUNT" />
                          </RequireFlag>
                        }
                      />
                    </Route>
                  </Route>
                  <Route path="documents" element={<YourDocuments />} />
                </Route>
                <Route path="stocks" element={<Stocks />} />
                <Route
                  path="*"
                  element={
                    <div>
                      <h1>404 Page Not Found</h1>
                    </div>
                  }
                />
              </Route>
            </Routes>
          </Suspense>
        </Wrapper>
      </AppOverlaySection>

      <NotificationContainer />
      <NotifContainerV2 />
    </>
  );
}

export const App = withLDProvider({
  clientSideID: environment.LAUNCH_DARKLY_CLIENT_ID
})(InnerApp);
